// ============================================================================
// CreateClientDrawer — the NEW-CLIENT form in a slide-over Drawer.
// ----------------------------------------------------------------------------
// Optional-first: a NAME is the only required field — everything else can be
// filled later from the client's detail page. Fields map straight to ClientIn:
//   • name (required)        → TextField
//   • sex (m/f/unspecified)  → SelectField (design-system dropdown)
//   • birthday               → DateField (calendar popup)
//   • height_cm              → numeric TextField with a "cm" suffix
//   • occupation             → TextField
//   • remarks                → textarea (styled to match TextField)
//   • weekly availability    → structured AvailabilityPicker (lib.ts codec)
//   • OPTIONAL first goal     → a collapsible block (kind + target + date)
// Submits via useCreateClient; when an optional goal is present it chains a
// useCreateGoal against the freshly-created client id. Both are live-synced, so
// the new client appears on every device with no reload.
// ============================================================================
import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Banner,
  Button,
  DateField,
  PhoneField,
  SelectField,
  TextField,
  isE164,
} from "../../components";
import { useCreateClient } from "../../hooks/clients";
import { useCreateGoal } from "../../hooks/goals";
import type { Client, ClientInput, GoalInput } from "../../api";
import { cn } from "../../lib/cn";
import { isEmail } from "../../lib/validation";
import { Drawer } from "./Drawer";
import { AvailabilityPicker } from "./Availability";
import {
  ACTIVITY_LEVELS,
  emptyAvailability,
  serializeAvailability,
  type ActivityLevelCode,
  type SexCode,
  type WeeklyAvailability,
} from "./lib";

interface CreateClientDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Called with the created client so the parent can tap straight through. */
  onCreated: (client: Client) => void;
}

const METRIC_OPTIONS = ["weight", "body_fat", "bmi", "whr", "whtr"] as const;

export function CreateClientDrawer({
  open,
  onClose,
  onCreated,
}: CreateClientDrawerProps) {
  const { t } = useTranslation("clients");
  const reduce = useReducedMotion();
  const createClient = useCreateClient();
  const createGoal = useCreateGoal();

  // --- profile fields ---
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sex, setSex] = useState<SexCode>("");
  const [birthday, setBirthday] = useState("");
  const [height, setHeight] = useState("");
  const [activityLevel, setActivityLevel] = useState<ActivityLevelCode>("");
  const [occupation, setOccupation] = useState("");
  const [remarks, setRemarks] = useState("");
  const [weekly, setWeekly] = useState<WeeklyAvailability>(emptyAvailability());
  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // --- optional portal password ---
  const [portalPasswordOpen, setPortalPasswordOpen] = useState(false);
  const [portalPassword, setPortalPassword] = useState("");
  const [portalPasswordError, setPortalPasswordError] = useState<string | null>(null);

  // --- optional first goal ---
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalKind, setGoalKind] = useState("");
  const [goalMetric, setGoalMetric] = useState("weight");
  const [goalTarget, setGoalTarget] = useState("");
  const [goalDate, setGoalDate] = useState("");

  const pending = createClient.isPending || createGoal.isPending;

  // Reset the form to a clean slate whenever it (re)opens.
  useEffect(() => {
    if (!open) return;
    setName("");
    setPhone("");
    setEmail("");
    setSex("");
    setBirthday("");
    setHeight("");
    setActivityLevel("");
    setOccupation("");
    setRemarks("");
    setWeekly(emptyAvailability());
    setNameError(null);
    setPhoneError(null);
    setEmailError(null);
    setPortalPasswordOpen(false);
    setPortalPassword("");
    setPortalPasswordError(null);
    setGoalOpen(false);
    setGoalKind("");
    setGoalMetric("weight");
    setGoalTarget("");
    setGoalDate("");
  }, [open]);

  const num = (s: string): number | null => {
    const v = s.trim();
    if (v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const submit = () => {
    // Required-field validation: name, phone (E.164), email. Collect all errors
    // before bailing so the user sees every problem at once.
    let ok = true;
    if (!name.trim()) {
      setNameError(t("form.nameRequired"));
      ok = false;
    }
    const emailTrimmed = email.trim();
    if (!emailTrimmed) {
      setEmailError(t("form.emailRequired"));
      ok = false;
    } else if (!isEmail(emailTrimmed)) {
      setEmailError(t("form.emailInvalid"));
      ok = false;
    }
    if (!phone.trim()) {
      setPhoneError(t("form.phoneRequired"));
      ok = false;
    } else if (!isE164(phone)) {
      setPhoneError(t("form.phoneInvalid"));
      ok = false;
    }
    const pwTrimmed = portalPassword.trim();
    if (pwTrimmed && pwTrimmed.length < 8) {
      setPortalPasswordError(t("form.portalPasswordTooShort"));
      ok = false;
    }
    if (!ok) return;

    const heightNum = num(height);
    const body: ClientInput = {
      name: name.trim(),
      phone: phone.trim(),
      email: emailTrimmed,
      sex: sex || null,
      birthday: birthday || null,
      height_cm: heightNum,
      activity_level: activityLevel || null,
      occupation: occupation.trim() || null,
      remarks: remarks.trim() || null,
      availability: serializeAvailability(weekly),
      ...(pwTrimmed ? { portal_password: pwTrimmed } : {}),
    };

    // Capture the optional first goal up-front (form is reset on close).
    const targetNum = num(goalTarget);
    const wantsGoal =
      goalOpen && (goalKind.trim() !== "" || targetNum != null || goalDate !== "");

    createClient.mutate(body, {
      onSuccess: (client) => {
        if (!wantsGoal) {
          onCreated(client);
          onClose();
          return;
        }
        const goalBody: GoalInput = {
          kind: goalKind.trim() || null,
          metric_key: goalMetric || null,
          target_value: targetNum,
          target_date: goalDate || null,
        };
        createGoal.mutate(
          { clientId: client.id, body: goalBody },
          {
            // The client exists either way — never block the hand-off on the
            // optional goal. On success or failure we still tap through.
            onSettled: () => {
              onCreated(client);
              onClose();
            },
          },
        );
      },
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("form.title")}
      subtitle={t("form.subtitle")}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? t("actions.creating") : t("actions.create")}
          </Button>
        </div>
      }
    >
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t("form.name")}
          value={name}
          required
          placeholder={t("form.namePlaceholder")}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          error={nameError ?? undefined}
          autoComplete="off"
          autoFocus
        />

        <TextField
          label={t("form.email")}
          value={email}
          required
          type="email"
          inputMode="email"
          placeholder={t("form.emailPlaceholder")}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          error={emailError ?? undefined}
          autoComplete="off"
        />

        <PhoneField
          label={t("form.phone")}
          countryLabel={t("form.phoneCountry")}
          value={phone}
          required
          hint={t("form.phoneHint")}
          onChange={(v) => {
            setPhone(v);
            if (phoneError) setPhoneError(null);
          }}
          error={phoneError ?? undefined}
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SelectField
            label={t("form.sex")}
            value={sex}
            onChange={(e) => setSex(e.target.value as SexCode)}
            options={[
              { value: "", label: t("sex.unspecified") },
              { value: "m", label: t("sex.m") },
              { value: "f", label: t("sex.f") },
            ]}
          />
          <DateField
            label={t("form.birthday")}
            value={birthday}
            onChange={setBirthday}
          />
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <TextField
            label={t("form.height")}
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            type="number"
            inputMode="decimal"
            min={0}
            step="0.1"
            suffix="cm"
          />
          <SelectField
            label={t("form.activityLevel")}
            value={activityLevel}
            onChange={(e) =>
              setActivityLevel(e.target.value as ActivityLevelCode)
            }
            options={[
              { value: "", label: t("activity:unset", "Not set") },
              ...ACTIVITY_LEVELS.map((lvl) => ({
                value: lvl,
                label: t(`activity:level.${lvl}`, lvl),
              })),
            ]}
          />
        </div>

        <TextField
          label={t("form.occupation")}
          value={occupation}
          onChange={(e) => setOccupation(e.target.value)}
          autoComplete="off"
        />

        <div className="w-full">
          <label
            htmlFor="new-client-remarks"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("form.remarks")}
          </label>
          <textarea
            id="new-client-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            placeholder={t("form.remarksPlaceholder")}
            className={cn(
              "w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint",
              "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast",
              "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none",
            )}
          />
        </div>

        <AvailabilityPicker value={weekly} onChange={setWeekly} />

        {/* ---- optional portal password ---------------------------------- */}
        <div className="rounded-md ring-1 ring-line">
          <button
            type="button"
            onClick={() => setPortalPasswordOpen((o) => !o)}
            aria-expanded={portalPasswordOpen}
            className="flex w-full items-center justify-between gap-3 rounded-md px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
          >
            <span className="min-w-0">
              <span className="block font-display text-sm font-bold text-body">
                {t("form.portalPasswordToggle")}
              </span>
              <span className="block text-xs text-body-soft">
                {t("form.portalPasswordToggleHint")}
              </span>
            </span>
            <motion.span
              animate={reduce ? undefined : { rotate: portalPasswordOpen ? 180 : 0 }}
              transition={{ duration: 0.18 }}
              className="shrink-0 text-body-muted"
              aria-hidden
            >
              <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none">
                <path
                  d="M5 7.5 10 12.5 15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.span>
          </button>

          <AnimatePresence initial={false}>
            {portalPasswordOpen && (
              <motion.div
                initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
                animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="border-t border-line px-4 py-4">
                  <TextField
                    label={t("form.portalPassword")}
                    value={portalPassword}
                    type="password"
                    hint={t("form.portalPasswordHint")}
                    onChange={(e) => {
                      setPortalPassword(e.target.value);
                      if (portalPasswordError) setPortalPasswordError(null);
                    }}
                    error={portalPasswordError ?? undefined}
                    autoComplete="new-password"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ---- optional first goal -------------------------------------- */}
        <div className="rounded-md ring-1 ring-line">
          <button
            type="button"
            onClick={() => setGoalOpen((o) => !o)}
            aria-expanded={goalOpen}
            className="flex w-full items-center justify-between gap-3 rounded-md px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
          >
            <span className="min-w-0">
              <span className="block font-display text-sm font-bold text-body">
                {t("form.goalToggle")}
              </span>
              <span className="block text-xs text-body-soft">
                {t("form.goalToggleHint")}
              </span>
            </span>
            <motion.span
              animate={reduce ? undefined : { rotate: goalOpen ? 180 : 0 }}
              transition={{ duration: 0.18 }}
              className="shrink-0 text-body-muted"
              aria-hidden
            >
              <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none">
                <path
                  d="M5 7.5 10 12.5 15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.span>
          </button>

          <AnimatePresence initial={false}>
            {goalOpen && (
              <motion.div
                initial={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
                animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="space-y-5 border-t border-line px-4 py-4">
                  <TextField
                    label={t("form.goalKind")}
                    value={goalKind}
                    onChange={(e) => setGoalKind(e.target.value)}
                    placeholder={t("form.goalKindPlaceholder")}
                    autoComplete="off"
                  />
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <SelectField
                      label={t("form.goalMetric")}
                      value={goalMetric}
                      onChange={(e) => setGoalMetric(e.target.value)}
                      options={METRIC_OPTIONS.map((m) => ({
                        value: m,
                        label: t(`metric.${m}`),
                      }))}
                    />
                    <TextField
                      label={t("form.goalTarget")}
                      value={goalTarget}
                      onChange={(e) => setGoalTarget(e.target.value)}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                    />
                  </div>
                  <DateField
                    label={t("form.goalDate")}
                    value={goalDate}
                    onChange={setGoalDate}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {createClient.isError && (
          <Banner tone="danger" title={t("form.saveError")}>
            {t("form.saveErrorBlurb")}
          </Banner>
        )}
      </form>
    </Drawer>
  );
}
